import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Alert } from "@/atoms";
import type { RegisterFormFieldsInterface } from "@/components/auth";
import {
    AuthHeader,
    AuthInvalidState,
    AuthSuccessState,
    RegisterForm,
} from "@/components/auth";
import { routes, strings } from "@/constants";
import { AlertVariantEnum } from "@/enums";
import { useAcceptInvitation } from "@/hooks";
import { AuthLayout } from "@/layouts";
import { validateEmailHandler, validatePasswordHandler } from "@/utils";

const initialFields: RegisterFormFieldsInterface = {
    companyName: "",
    confirmPassword: "",
    email: "",
    name: "",
    password: "",
    phone: "",
};

export const RegisterPage = () => {
    const [token, setToken] = useState("");

    const [fields, setFields] = useState<RegisterFormFieldsInterface>(initialFields);

    const [validationError, setValidationError] = useState("");

    const [isSuccess, setIsSuccess] = useState(false);

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const {
        error: serviceError,
        isLoading,
        onRun: runAcceptInvitationHandler,
    } = useAcceptInvitation();

    const error = validationError || serviceError || "";

    const validateFormHandler = (): string | null => {
        if (!fields.name.trim()) return strings.validation.nameRequired;

        const emailValidation = validateEmailHandler(fields.email);

        if (!emailValidation.isValid) return emailValidation.error || strings.validation.emailInvalid;

        if (!fields.companyName.trim()) return strings.validation.companyNameRequired;

        if (!fields.phone.trim()) return strings.validation.phoneRequired;

        const passwordValidation = validatePasswordHandler(fields.password);

        if (!passwordValidation.isValid) return passwordValidation.error || strings.errors.checkInput;

        if (fields.password !== fields.confirmPassword) return strings.register.passwordMismatch;

        return null;
    };

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setValidationError("");

        const formError = validateFormHandler();

        if (formError) {
            setValidationError(formError);

            return;
        }

        try {
            await runAcceptInvitationHandler(
                token,
                {
                    companyName: fields.companyName,
                    email: fields.email,
                    name: fields.name,
                    password: fields.password,
                    passwordConfirmation: fields.confirmPassword,
                    phone: fields.phone,
                },
            );

            setIsSuccess(true);
        } catch {
            // Error captured by hook
        }
    };

    const backToLoginHandler = () => navigate(routes.login);

    const changeFieldHandler = (key: keyof RegisterFormFieldsInterface, value: string) => {
        setFields((prev) => ({
            ...prev,
            [key]: value,
        }));

        setValidationError("");
    };

    useEffect(
        () => {
            const tokenParam = searchParams.get(strings.queryParams.token);

            if (tokenParam) setToken(tokenParam);
        },
        [searchParams],
    );

    if (isSuccess) {
        return (
            <AuthLayout
                description={strings.register.sidebarDescription}
                title={strings.register.sidebarTitle}
            >
                <AuthSuccessState
                    actionLabel={strings.register.goToLogin}
                    message={strings.register.successMessage}
                    title={strings.register.title}
                    isFullWidth
                    onAction={backToLoginHandler}
                />
            </AuthLayout>
        );
    }

    if (!token) {
        return (
            <AuthLayout
                description={strings.register.sidebarDescription}
                title={strings.register.sidebarTitle}
            >
                <AuthInvalidState
                    actionLabel={strings.register.backToLogin}
                    message={strings.register.invalidToken}
                    title={strings.register.title}
                    onAction={backToLoginHandler}
                />
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            description={strings.register.sidebarDescription}
            title={strings.register.sidebarTitle}
        >
            <div>
                <AuthHeader
                    description={strings.register.description}
                    title={strings.register.title}
                />
                {error && (
                    <Alert
                        className="mb-6"
                        variant={AlertVariantEnum.ERROR}
                        onClose={() => setValidationError("")}
                    >
                        {error}
                    </Alert>
                )}
                <RegisterForm
                    error={error}
                    fields={fields}
                    isLoading={isLoading}
                    onBackToLogin={backToLoginHandler}
                    onChangeField={changeFieldHandler}
                    onSubmit={submitHandler}
                />
            </div>
        </AuthLayout>
    );
};
